import { SAVE_SCHEMA_VERSION } from "../../config/gameplay";
import { createEmptyUpgradeLevels } from "../upgrades/definitions";
import { OVERDRIVE_RULES } from "../../config/gameplay";
import type { AutoLaunchState, CosmeticState, OrbitForgeSave, PeriodState, PlayerSettings, ProgressStats, SupplyCrateState, TimedEventState } from "./types";

export function createFreshSave(nowUnixSeconds: number): OrbitForgeSave {
  return {
    schemaVersion: SAVE_SCHEMA_VERSION,
    createdAtUnixSeconds: nowUnixSeconds,
    updatedAtUnixSeconds: nowUnixSeconds,
    lastSeenUnixSeconds: nowUnixSeconds,
    rngState: 0x8f17_2026,
    nextEntityId: 1,
    previousProgressScore: 0,
    tutorialDismissed: false,
    stats: createEmptyStats(),
    upgrades: createEmptyUpgradeLevels(),
    cosmetics: createDefaultCosmetics(),
    crates: createDefaultCrateState(),
    autoLaunch: createDefaultAutoLaunchState(),
    events: createDefaultTimedEventState(),
    settings: createDefaultSettings(),
    periods: periodForUnixSeconds(nowUnixSeconds)
  };
}

export function createEmptyStats(): ProgressStats {
  return {
    Credits: 0,
    TotalEarnedCredits: 0,
    DailyEarnedCredits: 0,
    WeeklyEarnedCredits: 0,
    TotalPlayTimeSeconds: 0,
    DailyPlayTimeSeconds: 0,
    WeeklyPlayTimeSeconds: 0,
    TotalCoresLaunched: 0,
    DailyCoresLaunched: 0,
    WeeklyCoresLaunched: 0,
    TotalCoresCollected: 0,
    DailyCoresCollected: 0,
    WeeklyCoresCollected: 0,
    TotalChargeBounces: 0,
    DailyChargeBounces: 0,
    WeeklyChargeBounces: 0,
    BestMultiplier: 1,
    BestPolarityStack: 0,
    OverchargedCoresLaunched: 0,
    ReactorStormEventsCompleted: 0,
    ReactorStormMaxCompletedCores: 0,
    SupplyCratesOpened: 0,
    FreeSupplyCratesOpened: 0,
    SupplyJackpotsWon: 0,
    CircuitDuelWins: 0,
    CircuitDuelCreditsWon: 0,
    FluxHockeyWins: 0,
    FluxHockeyMatches: 0,
    FluxHockeyCreditsWon: 0,
    FluxHockeyXp: 0,
    AutoLaunchTickets60: 0,
    AutoLaunchTickets120: 0,
    AutoLaunchTickets300: 0,
    AutoLaunchTickets600: 0,
    AutoLaunchPurchases60: 0,
    AutoLaunchPurchases120: 0,
    AutoLaunchPurchases300: 0,
    AutoLaunchPurchases600: 0,
    AutoLaunchDurationLevel: 0,
    AutoLaunchRateLevel: 0,
    OfflineRewardClaims: 0,
    DailyIgnitionClaims: 0,
    DailyIgnitionStreak: 0,
    DailyIgnitionLastDayId: 0,
    DailyIgnitionRemainingSeconds: 0,
    DailyIgnitionActive: 0,
    LuckySparksClaimed: 0
  };
}

export function createDefaultCosmetics(): CosmeticState {
  return {
    SelectedShellSkin: "white_arc",
    SelectedCorePattern: "clean_core",
    UnlockedCrateShellSkins: [],
    UnlockedCrateCorePatterns: [],
    PrestigeSquareModeEnabled: false,
    PrestigeArenaPreferenceSet: false,
    UnlockedAchievements: []
  };
}

export function createDefaultSettings(): PlayerSettings {
  return {};
}

export function createDefaultCrateState(): SupplyCrateState {
  return {
    freeReadyAtUnixSeconds: 0,
    paidOpenWindows: {
      mini: { startUnixSeconds: 0, opens: 0 },
      neon: { startUnixSeconds: 0, opens: 0 },
      jackpot: { startUnixSeconds: 0, opens: 0 }
    },
    pendingResult: null,
    lastResult: null
  };
}

export function createDefaultAutoLaunchState(): AutoLaunchState {
  return {
    activeTicketSeconds: null,
    activeRemainingSeconds: 0,
    autoExtendEnabled: false,
    spawnBudget: 0
  };
}

export function createDefaultTimedEventState(): TimedEventState {
  return {
    overdrive: {
      activeKind: null,
      activeSeconds: 0,
      cooldownSeconds: OVERDRIVE_RULES.cooldownSeconds,
      prestigeWindowStartedAtUnixSeconds: 0,
      prestigeUsesInWindow: 0
    },
    reactorStorm: {
      cooldownSeconds: 0
    }
  };
}

export function periodForUnixSeconds(unixSeconds: number): PeriodState {
  const dayId = Math.floor(unixSeconds / 86_400);
  return {
    dayId,
    weekId: Math.floor(dayId / 7)
  };
}
